import React, { useState } from 'react';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';

import { Button, LoadingButton, Modal } from '@common/index';
import { Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, State, flowActions } from '@store/index';
import { useTheme } from '../../styles/theme';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { Label } from '@common/Label';
import { useSecrets } from '@common/hooks/useSecrets';
import { RequestError } from '@requests/common';
import {
  createSecret as createSecretRequest,
  IntegrationSecret,
  IntegrationSecretCreateRequest,
} from '@requests/secrets';

type Secret = IntegrationSecretCreateRequest<'basic' | 'bearer'>;

const initialSecret: Secret = {
  identifier: '',
  type: 'bearer',
  username: '',
  password: '',
  token: '',
};

export const CreateSecret: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { secrets, loading } = useSecrets();

  const createSecret = useSelector((state: State) => state.flow.createSecret);

  const [secretType, setSecretType] = useState<'basic' | 'bearer'>('bearer');
  const [secret, setSecret] = useState<Secret>(initialSecret);

  const { mutateAsync: createSecretVariable, isPending: isUpsertLoading } = useMutation<
    IntegrationSecret,
    RequestError,
    { serviceId: string; secret: IntegrationSecretCreateRequest<'basic' | 'bearer'> }
  >({
    mutationFn: createSecretRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-secrets'] });
      enqueueSnackbar('Secret created!', { variant: 'success', autoHideDuration: 3000 });
      closeModal();
    },
    onError: (error) => {
      enqueueSnackbar('Failed to create secrets!', {
        variant: 'error',
        autoHideDuration: 10000,
      });

      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const formatSecretIdentifier = (identifier: string) => {
    const lowercase = identifier.toLowerCase();
    return lowercase.replace(/[^a-z0-9_-]/g, '');
  };

  const handlePropChange = (prop: keyof Secret) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (prop === 'identifier') {
      setSecret({ ...secret, [prop]: formatSecretIdentifier(e.target.value) });
    } else {
      setSecret({ ...secret, [prop]: e.target.value });
    }
  };

  const closeModal = () => {
    dispatch(flowActions.setCreateSecret(false));
    setSecret(initialSecret);
    setSecretType('bearer');
  };

  const handleAddSecret = async () => {
    try {
      if (!service_id) {
        throw new Error('Service ID is required!');
      }
      // check if secret identifier already exists
      if (secrets.some((s) => s.identifier === secret.identifier)) {
        throw new Error('Secret with this name already exists!');
      }
      // check if secret name is empty
      if (!secret.identifier || secret.identifier === '') {
        throw new Error('Secret name is required!');
      }
      /**
       * Check if the secret type is basic
       * If it is, check if the username and password are provided
       * If it is not, check if the token is provided
       */
      if (secretType === 'basic') {
        if (!secret.username || secret.username === '') {
          throw new Error('Username is required!');
        }
        if (!secret.password || secret.password === '') {
          throw new Error('Password is required!');
        }
      } else {
        if (!secret.token || secret.token === '') {
          throw new Error('Token is required!');
        }
      }

      try {
        await createSecretVariable({ serviceId: service_id, secret });
      } catch {}
    } catch (error: any) {
      enqueueSnackbar(error?.message as string, {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const handleSecretTypeChange = (e: React.MouseEvent<HTMLElement>, value: 'basic' | 'bearer' | null) => {
    if (!value) return;
    setSecretType(value);
    setSecret({ ...secret, type: value });
  };

  return (
    <Modal
      open={Boolean(createSecret)}
      setOpen={closeModal}
      header='Create Secret'
      background={theme.primary}
      icon={<KeyOutlinedIcon />}
      info={
        'Create a secret to be used in workflows and external Telnyx integrations. Secrets are account-level resources that can be accessed from any workspace.'
      }
      body={
        <Stack spacing={2} flexDirection='column'>
          <Stack spacing={1}>
            <Label
              title='Secret Type'
              tooltip={`Select the type of secret you want to create. Basic secrets can be created using a username and password, while bearer tokens can be created using a token.`}
            />
            <ToggleButtonGroup
              color='primary'
              value={secretType}
              exclusive
              fullWidth
              onChange={handleSecretTypeChange}
              aria-required={true}
            >
              <ToggleButton value='bearer'>Bearer Auth</ToggleButton>
              <ToggleButton value='basic'>Basic Auth</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack spacing={1}>
            <Label title='Name' tooltip='This is the API key name. For example, INTERCOM_API_KEY' />
            <TextField
              variant='outlined'
              value={secret.identifier}
              onChange={handlePropChange('identifier')}
              placeholder='Secret Name'
              fullWidth={true}
              required
            />
          </Stack>
          {secretType === 'basic' && (
            <Stack direction='row' spacing={1} justifyContent='space-between'>
              <Stack spacing={1} flexGrow={1}>
                <Label title='Username' tooltip='Username for basic authentication' />
                <TextField
                  variant='outlined'
                  value={secret.username}
                  onChange={handlePropChange('username')}
                  placeholder='Username'
                  fullWidth={true}
                  required
                />
              </Stack>
              <Stack spacing={1} flexGrow={1}>
                <Label title='Password' tooltip='Password for basic authentication' />
                <TextField
                  variant='outlined'
                  value={secret.password}
                  onChange={handlePropChange('password')}
                  placeholder='Password'
                  fullWidth={true}
                  required
                />
              </Stack>
            </Stack>
          )}
          {secretType === 'bearer' && (
            <Stack spacing={1}>
              <Label
                title='Token'
                tooltip='This is the value of your API key. This will be injected as the authentication parameter when used.'
              />
              <TextField
                variant='outlined'
                value={secret.token}
                onChange={handlePropChange('token')}
                placeholder='Token'
                fullWidth={true}
                required
              />
            </Stack>
          )}
        </Stack>
      }
      actions={
        <Stack spacing={1} direction='row'>
          <Button size='medium' onClick={closeModal}>
            Cancel
          </Button>
          <LoadingButton
            size='medium'
            customColor='primary'
            minWidth='75px'
            onClick={handleAddSecret}
            disabled={loading}
            loading={isUpsertLoading}
          >
            Create
          </LoadingButton>
        </Stack>
      }
    />
  );
};
